import { Component, ViewEncapsulation, Input, EventEmitter, Output, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalService } from '@shopping-angular-components/modal';
import { TooltipModule, TooltipComponent } from '@shopping-angular-components/tooltip';
import { CommonModule } from '@angular/common';
import { OfferSortingOption } from '../offers-filters/offers-filters-model';
import { SortingModalContentComponent } from './sorting-modal-content.component';
import { LandingModelService } from '../../services/landingModel.service';
import { ScreenType } from '../../model/ScreenType';

@Component({
  selector: 'sorting-select',
  template: `
    <ng-container *ngIf="useModal; else tooltipMode">
      <div class="sorting-select-container" (click)="openSortingOptions()">
        <div class="sorting-content">
          <div class="sorting-text-container">
            <span class="eva-3-overline-2">
              {{'common.sortBy' | translate}}
            </span>
            <span class="eva-3-caption">
              {{getSelectedOption()}}
            </span>
          </div>
          <i class="eva-3-icon-chevron-down -eva-3-icon-xsm"></i>
        </div>
      </div>
    </ng-container>

    <ng-template #tooltipMode>
      <tooltip 
        #sortingTooltip
        position='-bottom -right' 
        backgroundColor="-white"
        [openOnClick]="true" 
        [closeOnOutsideClick]="true"
        flow="landing" 
        product="nymeria-gui">
        
        <div label class="sorting-select-container">
          <div class="sorting-content">
            <div class="sorting-text-container">
              <span class="eva-3-overline-2">
                {{'common.sortBy' | translate}}
              </span>
              <span class="eva-3-caption">
                {{getSelectedOption()}}
              </span>
            </div>
            <i class="eva-3-icon-chevron-down -eva-3-icon-xsm"></i>
          </div>
        </div>

        <div tooltip-content class="sorting-tooltip-content">
          <div class="sorting-options-list">
            <div 
              class="sorting-option-item"
              *ngFor="let option of sortingOptions"
              (click)="selectOption(option)">
              <span class="option-label" [ngClass]="{'option-label-selected': option.selected}">
                {{option.name}}
              </span>
              <i *ngIf="option.selected" class="eva-3-icon-checkmark -eva-3-icon-sm option-checkmark"></i>
            </div>
          </div>
        </div>
      </tooltip>
    </ng-template>
  `,
  styleUrls: ['./sorting-select.component.scss'],
  standalone: true,
  imports: [TranslateModule, CommonModule, TooltipModule],
  encapsulation: ViewEncapsulation.None
})
export class SortingSelectComponent {

  @Input() sortingOptions: OfferSortingOption[] = [];
  @Input() moduleId: string = '';
  @Input() moduleIndex: number = 0;
  @Input() uniqueId?: string;
  @Input() useModal: boolean = false;

  @Output() sortingChangeEvent = new EventEmitter<OfferSortingOption>();

  @ViewChild('sortingTooltip') sortingTooltip: TooltipComponent;

  private isWebview = false;
  private isMobile = false;

  constructor(
    private modalService: ModalService,
    private translateService: TranslateService,
    private landingModelService: LandingModelService
  ) {
    const landingModel = this.landingModelService.getLandingModel();
    this.isWebview = landingModel.webview;
    this.isMobile = landingModel.device.type === ScreenType.MOBILE;
  }

  getSelectedOption(): string {
    const selected = this.sortingOptions.find(option => option.selected);
    return selected ? selected.name : '';
  }

  openSortingOptions(): void {
    if (this.useModal) {
      this.showSortingModal();
    }
  }

  selectOption(selectedOption: OfferSortingOption): void {
    if (!this.useModal && this.sortingTooltip?.isOpen) {
      this.sortingTooltip.toggleOpen();
    }

    this.sortingOptions.forEach(option => {
      option.selected = false;
    });

    selectedOption.selected = true;

    this.onSortingChange(selectedOption);
  }

  private showSortingModal(): void {
    this.modalService.showModal({
      id: 'sorting-modal',
      title: this.translateService.instant('common.sort'),
      product: 'nymeria-gui',
      flow: 'landing',
      isWebview: this.isWebview,
      contentComponent: SortingModalContentComponent,
      contentModel: {
        sortingOptions: this.sortingOptions,
        moduleIndex: this.moduleIndex,
        moduleId: this.moduleId,
        uniqueId: this.uniqueId,
        isMobile: this.isMobile,
        sortingChangeCallback: (selectedOption: OfferSortingOption) => this.onSortingChange(selectedOption)
      }
    });
  }

  onSortingChange(selectedOption: OfferSortingOption): void {
    this.sortingChangeEvent.emit(selectedOption);

    if (this.useModal) {
      this.modalService.closeModal();
    }
  }

}
